/**
 * Desktop bridge accessor — typed access to `window.taqatDesktop`, the API the
 * Electron shell's preload exposes for hybrid CoWork (Model C). Returns null in
 * the web app, so callers can cheaply gate desktop-only UI on `isDesktopApp()`.
 *
 * @module lib/desktop
 */

export interface DesktopStatus {
  connected: boolean;
  deviceId: string;
  folderPath: string | null;
}

export interface OpenFolderResult {
  deviceId?: string;
  folderName?: string;
  folderPath?: string;
  canceled?: boolean;
}

/** How autonomously the agent may act (composer selector). */
export type Autonomy = "ask" | "auto" | "full";

/** Where commands run (native host vs WSL2 Linux VM). */
export type ExecTier = "native" | "wsl2";

export interface DeviceCapabilities {
  platform: string;
  wsl2: { available: boolean; distro?: string };
}

export interface ApprovalHunk {
  index: number;
  header: string;
  lines: string[];
}

export interface ApprovalRequest {
  id: string;
  kind: "write" | "delete" | "mkdir" | "exec";
  path?: string;
  command?: string;
  summary: string;
  hunks?: ApprovalHunk[];
  isEdit?: boolean;
}

export interface ApprovalDecision {
  approved: boolean;
  acceptedHunks?: number[];
}

export interface FileEntry {
  name: string;
  path: string;
  isDirectory: boolean;
}

export interface WorkspaceChange {
  path: string;
  kind: "read" | "write" | "delete" | "mkdir" | "exec";
  at: number;
  undoable: boolean;
}

/**
 * Incremental change event (delta) streamed from the desktop shell. The device
 * sends one event per change rather than the whole array (reads are frequent).
 * `reset` replaces the set (hydration); steady state is `add`/`remove`.
 */
export type ChangeEvent =
  | { type: "add"; change: WorkspaceChange }
  | { type: "remove"; path: string }
  | { type: "reset"; changes: WorkspaceChange[] };

export interface TaqatDesktopApi {
  isDesktop: boolean;
  platform: string;
  versions?: { electron?: string; chrome?: string };
  cowork: {
    openFolder(): Promise<OpenFolderResult>;
    getRecentFolders(): Promise<{ folders: string[] }>;
    openRecent(folderPath: string): Promise<OpenFolderResult>;
    closeFolder(): Promise<{ ok: boolean }>;
    getStatus(): Promise<DesktopStatus>;
    onStatus(cb: (status: DesktopStatus) => void): () => void;
    setAuthToken(token: string | null): Promise<{ ok: boolean }>;
    setAutonomy(level: Autonomy): Promise<{ autonomy: Autonomy }>;
    getAutonomy(): Promise<{ autonomy: Autonomy }>;
    setExecTier(tier: ExecTier): Promise<{ execTier: ExecTier }>;
    getCapabilities(): Promise<DeviceCapabilities>;
    onApprovalRequest(cb: (req: ApprovalRequest) => void): () => void;
    respondApproval(id: string, decision: ApprovalDecision): Promise<{ ok: boolean }>;
    readFile(relPath: string): Promise<{ content?: string; error?: string }>;
    readFileBuffer(relPath: string): Promise<{ base64?: string; error?: string }>;
    listFiles(relPath: string): Promise<{ entries: FileEntry[]; error?: string }>;
    reveal(relPath: string, isDir: boolean): Promise<{ ok: boolean }>;
    getChanges(): Promise<WorkspaceChange[]>;
    undo(relPath: string): Promise<{ ok: boolean }>;
    onChanges(cb: (event: ChangeEvent) => void): () => void;
  };
}

/** The desktop bridge, or null when running in a normal browser. */
export function getDesktop(): TaqatDesktopApi | null {
  const w = window as unknown as { taqatDesktop?: TaqatDesktopApi };
  return w.taqatDesktop ?? null;
}

/** True only inside the Electron desktop shell. */
export function isDesktopApp(): boolean {
  return getDesktop()?.isDesktop === true;
}
